"use client";

import { Icon } from "@ospweb/sdk-ui";
import React, {
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useMemoizedFn } from "ahooks";
import { cn } from "utils";
import { Popover } from "@components/Popover";

export type SenderAttachmentSourceId =
  "photo" | "camera" | "files" | "second-opinion";

type SenderAttachmentSourceMenuVariant = "app" | "web";

const ATTACHMENT_MENU_ITEMS = [
  { id: "photo" as const, label: "Photo", icon: "image" },
  { id: "camera" as const, label: "Camera", icon: "camera" },
  { id: "files" as const, label: "Files", icon: "download-extension" },
] as const;

/** App / Web 共用：Second opinion */
const SECOND_OPINION_MENU_ITEM = {
  id: "second-opinion" as const,
  label: "Second opinion",
  icon: "ed",
} as const;

export type SenderAttachmentSourceMenuProps = {
  disabled?: boolean;
  /** App：Photo / Camera / Files；Web：仅 Files；均含 Second opinion */
  variant?: SenderAttachmentSourceMenuVariant;
  /** 已开启 Second opinion 时，菜单项不可再次点击 */
  secondOpinionDisabled?: boolean;
  onPickPhoto: () => void;
  onPickCamera: () => void;
  onPickFiles: () => void;
  onPickSecondOpinion: () => void;
  children: ReactNode;
};

export function SenderAttachmentSourceMenu({
  disabled = false,
  variant = "app",
  secondOpinionDisabled = false,
  onPickPhoto,
  onPickCamera,
  onPickFiles,
  onPickSecondOpinion,
  children,
}: SenderAttachmentSourceMenuProps) {
  const [open, setOpen] = useState(false);

  const handleOpenChange = useMemoizedFn((next: boolean) => {
    if (disabled) {
      setOpen(false);
      return;
    }
    setOpen(next);
  });

  const handleItemClick = useMemoizedFn((id: SenderAttachmentSourceId) => {
    if (id === "second-opinion" && secondOpinionDisabled) {
      return;
    }
    setOpen(false);
    if (id === "photo") {
      onPickPhoto();
      return;
    }
    if (id === "camera") {
      onPickCamera();
      return;
    }
    if (id === "second-opinion") {
      onPickSecondOpinion();
      return;
    }
    onPickFiles();
  });

  const visibleMenuItems = useMemo(() => {
    const attachmentItems =
      variant === "web"
        ? ATTACHMENT_MENU_ITEMS.filter((item) => item.id === "files")
        : ATTACHMENT_MENU_ITEMS;
    return [...attachmentItems, SECOND_OPINION_MENU_ITEM];
  }, [variant]);

  const menuContent = (
    <div
      className={cn(
        "flex flex-col items-stretch overflow-hidden rounded-2",
        "border border-border_white_transparent10",
        "bg-background_solid_neutral_primary p-2",
        "shadow-[0px_0px_32px_0px_rgba(0,0,0,0.05)]",
      )}
      role="menu"
    >
      {visibleMenuItems.map((item) => {
        const isSecondOpinionItem = item.id === "second-opinion";
        const isItemDisabled = isSecondOpinionItem && secondOpinionDisabled;

        return (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            disabled={isItemDisabled}
            aria-disabled={isItemDisabled}
            className={cn(
              "flex h-10 w-full shrink-0 items-center gap-2 overflow-hidden rounded-[6px] p-2 transition-colors",
              "body_medium_m text-content_primary",
              !isItemDisabled && "hover:bg-background_white_overlay5",
              isItemDisabled && "cursor-not-allowed opacity-40",
            )}
            onClick={() => handleItemClick(item.id)}
          >
            <Icon
              name={item.icon}
              size={16}
              className="shrink-0 text-content_primary"
              aria-hidden
            />
            <span className="whitespace-nowrap">{item.label}</span>
          </button>
        );
      })}
    </div>
  );

  const trigger = useMemo(() => {
    if (!React.isValidElement(children)) {
      return children;
    }
    return React.cloneElement(
      children as ReactElement<{
        "aria-expanded"?: boolean;
        "aria-haspopup"?: string;
      }>,
      {
        "aria-haspopup": "menu",
        "aria-expanded": disabled ? false : open,
      },
    );
  }, [children, disabled, open]);

  return (
    <Popover
      open={disabled ? false : open}
      onVisibilityChange={handleOpenChange}
      trigger="click"
      placement="bottom-start"
      content={menuContent}
      contentClassName="border-0 bg-transparent p-0 shadow-none"
      className="inline-flex"
    >
      {trigger}
    </Popover>
  );
}
